import { Maximize2, Minimize2, RefreshCw, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { GreenDot } from "@/components/ui/atoms/GreenDot";
import { Typography } from "@/components/ui/atoms/Typography";
import { Button } from "@/components/ui/atoms/Button";
import { IconWrapper } from "../ui/atoms";
import { BotAvatar } from "./BotAvatar";

interface ChatHeaderProps {
  onRefresh: () => void;
  onClose: () => void;
  /**
   * Optional mobile snap toggle.
   * Rendered as a small maximize/minimize icon button — a clear affordance
   * that tells the user "this changes size", not "this closes the panel".
   *
   * The drag handle in ChatbotWidget is the primary resize mechanism;
   * this is a secondary tap target for users who prefer buttons.
   */
  mobileSnapToggle?: {
    isExpanded: boolean;
    onToggle: () => void;
  };
}

export function ChatHeader({
  onRefresh,
  onClose,
  mobileSnapToggle,
}: ChatHeaderProps) {
  const { t } = useTranslation("chatbot");
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClose();
        }
      }}
      aria-label={t("header.aria_collapse")}
      className="flex cursor-pointer items-center justify-between border-b border-dark-invirtus-soft px-4 py-2.5 transition-colors hover:bg-dark-invirtus-soft/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-purple"
    >
      {/* Left: avatar + name (visual only; whole bar is the click target) */}
      <div className="flex items-center gap-2">
        <BotAvatar size="sm" />
        <div className="flex flex-col">
          <Typography variant="body-sm" weight="semibold" color="primary">
            {t("header.name")}
          </Typography>
          <div className="inline-flex items-center gap-1">
            <GreenDot pulse />
            <Typography variant="body-xs" color="muted" weight="medium">
              {t("header.status_online")}
            </Typography>
          </div>
        </div>
      </div>

      {/* Right: action buttons — stop click propagation so they don't also collapse the chat */}
      <div
        className="inline-flex items-center"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {/*
         * Mobile snap toggle — Maximize2 / Minimize2 icons are universally
         * understood as "resize" affordances, avoiding confusion with
         * ChevronUp/Down which feel like "scroll" or "open/close".
         */}
        {mobileSnapToggle ? (
          <Button
            type="icon"
            intent="muted"
            size="sm"
            className="sm:hidden"
            aria-label={mobileSnapToggle.isExpanded ? t("header.aria_minimize") : t("header.aria_maximize")}
            aria-pressed={mobileSnapToggle.isExpanded}
            icon={
              <IconWrapper size="sm">
                {mobileSnapToggle.isExpanded ? <Minimize2 /> : <Maximize2 />}
              </IconWrapper>
            }
            onClick={mobileSnapToggle.onToggle}
          />
        ) : null}

        <Button
          type="icon"
          intent="muted"
          size="sm"
          aria-label={t("header.aria_refresh")}
          icon={
            <IconWrapper size="sm">
              <RefreshCw />
            </IconWrapper>
          }
          onClick={onRefresh}
        />

        <Button
          type="icon"
          intent="muted"
          size="sm"
          aria-label={t("header.aria_close")}
          icon={
            <IconWrapper size="sm">
              <X />
            </IconWrapper>
          }
          onClick={onClose}
        />
      </div>
    </div>
  );
}